import _ from "lodash";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

import actions from "@/actions";

import ToolsBarContext from "../../ToolsBarContext";
import AddGroup from "./AddGroup";
import GroupDetail from "./GroupDetail";
import Icon from "./Icon";
import { GroupSelector } from "./selector";

const BackIcon = styled(Icon)`
    margin-right: 5px;
    font-weight: normal;
    border-radius: 5px;
    padding: 5px;
    visibility: ${({ $hide }) => ($hide ? "hidden" : "visible")};

    &:hover {
        background-color: #f2f2f2;
    }
`;

const titleAddGroup = "Add Group";
const titleModifyGeometry = "Modify Geometry";
const titleChooseGroup = "Choose Group";
const groupMode = "group";

const getTitle = (group, addingGroup) => {
    if (addingGroup) {
        return titleAddGroup;
    }

    return group ? titleModifyGeometry : titleChooseGroup;
};

const GroupPanel = ({
    selectedTool,
    geometries,
    settings,
    changeMode,
    addLightGroup,
}) => {
    const [group, setGroup] = useState(null);
    const [addingGroup, setAddingGroup] = useState(false);
    const [prevTool, setPrevTool] = useState(selectedTool);

    const reset = useCallback(() => {
        setGroup(null);
        setAddingGroup(false);
    }, []);

    if (selectedTool !== prevTool) {
        setPrevTool(selectedTool);
        reset();
    }

    const handleSelectGroup = useCallback(
        (g) => {
            if ((geometries[g.displayID] ?? []).length === 0) {
                changeMode(groupMode, { group: g });
                return;
            }

            setGroup(g);
        },
        [changeMode, geometries],
    );

    const handleAddGroup = useCallback(
        (grp) => {
            addLightGroup(grp);
            changeMode(groupMode, { group: grp });
            setAddingGroup(false);
        },
        [addLightGroup, changeMode],
    );

    const hasExistingGeometries =
        group && (geometries[group.displayID] ?? []).length > 0;
    const isBackVisible = addingGroup || hasExistingGeometries;

    return (
        <div className="group-wrapper">
            <div className="row header">
                <div className="col-11 title">
                    <BackIcon
                        className="fa fa-arrow-left"
                        onClick={reset}
                        $hide={!isBackVisible}
                    />
                    <span>{getTitle(group, addingGroup)}</span>
                </div>
                <ToolsBarContext.Consumer>
                    {(context) => (
                        <div
                            className="col-1 closebtn"
                            onClick={context.closePanel}
                        >
                            <span
                                aria-hidden="true"
                                style={{ cursor: "pointer" }}
                            >
                                &times;
                            </span>
                        </div>
                    )}
                </ToolsBarContext.Consumer>
            </div>
            {!addingGroup &&
                (hasExistingGeometries ? (
                    <GroupDetail group={group} geometries={geometries} />
                ) : (
                    <GroupSelector
                        onSelect={handleSelectGroup}
                        onAdd={() => setAddingGroup(true)}
                    />
                ))}
            {addingGroup && (
                <AddGroup
                    floorSpaceID={settings.dataAPI.floorSpaceID}
                    onAdd={handleAddGroup}
                />
            )}
        </div>
    );
};

GroupPanel.propTypes = {
    selectedTool: PropTypes.object,
    geometries: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired,
    changeMode: PropTypes.func.isRequired,
    addLightGroup: PropTypes.func.isRequired,
};

const mapStateToProps = ({ map }) => ({
    selectedTool: map.general.selectedTool,
    geometries: _.groupBy(
        map.values.global.present.groupGeometries,
        (g) => g.group.displayID,
    ),
    lightGroups: map.general.lightGroups,
    settings: map.general.settings,
});

const mapDispatchToProps = {
    changeMode: actions.map.general.changeMode,
    addLightGroup: actions.map.general.addLightGroup,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupPanel);
